<template>
  <v-form v-model="valid" @submit.prevent="submit">
    <v-card class="pa-4">
      <v-card-title class="px-0 d-flex align-center">
        <v-icon icon="mdi-calendar-clock" color="primary" class="mr-2" />
        {{
          edit_mode
            ? "Veldwerk verslag type bewerken"
            : "Nieuw veldwerk verslag type"
        }}
      </v-card-title>
      <v-card-text class="px-0">
        <v-text-field
          v-model="day_report_type_edit.name"
          label="Naam"
          :rules="[rules.required]"
          variant="outlined"
          density="comfortable"
          prepend-inner-icon="mdi-form-textbox"
          placeholder="Naam van het verslag type"
          class="mb-2"
        />

        <v-select
          v-model="day_report_type_edit.type"
          :items="day_report_type_types"
          item-title="name"
          item-value="id"
          label="Type registratie"
          :rules="[rules.required]"
          variant="outlined"
          density="comfortable"
          prepend-inner-icon="mdi-format-list-checks"
          placeholder="Selecteer type registratie"
          class="mb-2"
        />

        <div class="d-flex gap-2">
          <v-menu v-model="startTimeMenu" :close-on-content-click="false">
            <template #activator="{ props }">
              <v-text-field
                v-model="day_report_type_edit.start_time"
                label="Start tijd"
                :rules="[rules.required, rules.timeFormat]"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-clock-outline"
                placeholder="HH:mm"
                readonly
                v-bind="props"
                class="flex-grow-1"
              />
            </template>
            <v-time-picker
              v-model="day_report_type_edit.start_time"
              format="24hr"
              :allowed-minutes="allowedMinutes"
              @click:minute="startTimeMenu = false"
            />
          </v-menu>

          <v-menu v-model="durationMenu" :close-on-content-click="false">
            <template #activator="{ props }">
              <v-text-field
                v-model="durationDisplay"
                label="Duur"
                :rules="[rules.required, rules.timeFormat]"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-timer-outline"
                placeholder="HH:mm"
                readonly
                v-bind="props"
                class="flex-grow-1"
              />
            </template>
            <v-time-picker
              v-model="durationTimePickerValue"
              format="24hr"
              :allowed-minutes="allowedMinutes"
              @update:model-value="updateDurationFromTimePicker"
              @click:minute="durationMenu = false"
            />
          </v-menu>
        </div>

        <div class="d-flex gap-2 mt-2">
          <v-menu v-model="startDateMenu" :close-on-content-click="false">
            <template #activator="{ props }">
              <v-text-field
                v-model="computedStartDateFormatted"
                label="Geldig vanaf"
                :rules="[rules.required]"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-calendar"
                placeholder="DD-MM-YYYY"
                readonly
                v-bind="props"
                class="flex-grow-1"
              />
            </template>
            <v-date-picker
              v-model="day_report_type_edit.start_date"
              @update:model-value="startDateMenu = false"
            />
          </v-menu>

          <v-menu v-model="endDateMenu" :close-on-content-click="false">
            <template #activator="{ props }">
              <v-text-field
                v-model="computedEndDateFormatted"
                label="Geldig tot (optioneel)"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-calendar-end"
                placeholder="DD-MM-YYYY"
                readonly
                v-bind="props"
                class="flex-grow-1"
                clearable
                hint="Laat leeg voor onbeperkte geldigheid"
                persistent-hint
                @click:clear="day_report_type_edit.end_date = ''"
              />
            </template>
            <v-date-picker
              v-model="day_report_type_edit.end_date"
              @update:model-value="endDateMenu = false"
            />
          </v-menu>
        </div>
      </v-card-text>

      <v-card-actions class="px-0">
        <v-spacer />
        <v-btn
          color="primary"
          :loading="loading"
          :disabled="!valid"
          prepend-icon="mdi-content-save"
          @click="submit"
        >
          Opslaan
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-form>
</template>

<script>
import { ref, computed } from "vue";
import moment from "moment";
import { useFormsStore } from "@/stores/formsStore";
import { useDayReportTypesStoreRefactor } from "@/stores/dayReportTypesStoreRefactor";
import { useAlertStore } from "@/stores/alertStore";
import {
  formatDurationHHMM,
  parseDurationToSeconds,
} from "@/composables/useDurationFormatter";

export default {
  name: "DayReportTypeForm",
  props: {
    day_report_type: {
      type: Object,
      default: () => ({}),
    },
    popup: {
      type: Boolean,
      default: false,
    },
    modelValue: {
      type: Boolean,
      default: false,
    },
  },

  setup(props) {
    const formsStore = useFormsStore();
    const dayReportTypesStore = useDayReportTypesStoreRefactor();
    const alertStore = useAlertStore();

    const valid = ref(true);
    const loading = ref(false);
    const startTimeMenu = ref(false);
    const durationMenu = ref(false);
    const startDateMenu = ref(false);
    const endDateMenu = ref(false);

    const day_report_type_edit = ref({
      id: "",
      name: "",
      type: "",
      start_time: "",
      duration: 28800, // 8 hours in seconds as default
      start_date: moment().format("YYYY-MM-DD"),
      end_date: "",
      active: true,
      working: false,
      direct_time: false,
      ...props.day_report_type,
    });

    const day_report_type_types = [
      { id: "DayReportType::Working", name: "Werktijd" },
      { id: "DayReportType::Backup", name: "Backup" },
      { id: "DayReportType::NonWorking", name: "Geen werktijd" },
      { id: "DayReportType::Balance", name: "Balans" },
    ];

    const rules = {
      required: (value) => !!value || "Dit veld is verplicht.",
      timeFormat: (value) => {
        if (!value) return true;
        const pattern = /^([0-9]|0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/;
        return pattern.test(value) || "Tijd moet in formaat HH:mm zijn";
      },
    };

    const allowedMinutes = (minute) => {
      return minute % 15 === 0;
    };

    const computedStartDateFormatted = computed(() => {
      if (!day_report_type_edit.value.start_date) return "";
      return moment(day_report_type_edit.value.start_date).format("DD-MM-YYYY");
    });

    const computedEndDateFormatted = computed(() => {
      if (!day_report_type_edit.value.end_date) return "";
      return moment(day_report_type_edit.value.end_date).format("DD-MM-YYYY");
    });

    const edit_mode = computed(() => {
      return !!day_report_type_edit.value.id;
    });

    // Computed property for displaying duration in HH:MM format
    const durationDisplay = computed({
      get() {
        return formatDurationHHMM(day_report_type_edit.value.duration || 0);
      },
      set(value) {
        day_report_type_edit.value.duration = parseDurationToSeconds(value);
      },
    });

    // Computed property for time picker (converts seconds to HH:MM for picker)
    const durationTimePickerValue = computed({
      get() {
        return formatDurationHHMM(day_report_type_edit.value.duration || 0);
      },
      set() {
        // This will be handled by updateDurationFromTimePicker
      },
    });

    // Method to update duration from time picker
    const updateDurationFromTimePicker = (timeValue) => {
      if (timeValue) {
        day_report_type_edit.value.duration = parseDurationToSeconds(timeValue);
      }
    };

    const submit = async () => {
      if (!valid.value) return;

      loading.value = true;
      try {
        const action = day_report_type_edit.value.id
          ? dayReportTypesStore.update
          : dayReportTypesStore.create;

        await action(day_report_type_edit.value);

        alertStore.setAlert(
          `Veldwerk verslag type succesvol ${day_report_type_edit.value.id ? "bijgewerkt" : "aangemaakt"}`,
          "success",
        );

        await dayReportTypesStore.getAll();

        formsStore.$reset();
      } catch (error) {
        console.error("Error saving day report type:", error);
        alertStore.setAlert(
          "Er is een fout opgetreden bij het opslaan van het veldwerk verslag type",
          "error",
        );
      } finally {
        loading.value = false;
      }
    };

    return {
      valid,
      loading,
      startTimeMenu,
      durationMenu,
      startDateMenu,
      endDateMenu,
      day_report_type_edit,
      day_report_type_types,
      rules,
      allowedMinutes,
      computedStartDateFormatted,
      computedEndDateFormatted,
      edit_mode,
      submit,
      durationDisplay,
      durationTimePickerValue,
      updateDurationFromTimePicker,
      formatDurationHHMM,
    };
  },
};
</script>
